<template>
  <div class="view-wrapper d-flex flex-column" :class="darkMode ? 'theme-dark' : 'theme-light'">

    <div class="header px-5 py-3 flex-shrink-0">
      <v-row align="center" no-gutters>
        <v-col cols="5">
          <div class="responsive-header-title text-truncate">
            {{ $t('message.todays_classes') }}
          </div>
          <div class="responsive-header-subtitle">
            {{ $d(currentTime, 'month_day_year', $i18n.locale) }}
            <span v-if="classLocation"> &middot; {{ classLocation }}</span>
          </div>
        </v-col>

        <v-col cols="2" class="d-flex justify-center">
          <v-avatar v-if="academyLogo" @click="navigateTo({ name: 'checkin' })" size="8.5vw" class="elevation-4">
            <ImageDisplay :src="academyLogo" />
          </v-avatar>
        </v-col>

        <v-col cols="5" class="text-right">
          <span class="responsive-clock font-weight-black">{{ academyClock }}</span>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <!-- The schedule sits in its own column so a media panel can be added
         beside it later without moving any of this. -->
    <div class="main-content flex-grow-1 px-4 pt-2 d-flex">
      <div class="schedule-pane flex-grow-1">
        <div v-if="!todayClasses.length" class="empty-state d-flex flex-column align-center justify-center fill-height">
          <v-avatar v-if="academyLogo" size="18vw" class="mb-6 elevation-4">
            <ImageDisplay :src="academyLogo" />
          </v-avatar>
          <div class="responsive-subheader">{{ $t('message.no_classes_today') }}</div>
        </div>

        <div v-else class="schedule-column-flow" ref="scheduleContainer">
          <v-card outlined class="schedule-item" v-for="(c, index) in todayClasses" :key="'class_' + index"
            :class="{ 'is-active': c.isActive, 'is-finished': c.isPast }">
            <div class="d-flex align-center pa-3 fill-height">
              <div class="time-block mr-4 text-center flex-shrink-0">
                <div class="responsive-time font-weight-black">{{ classTime(c.startDate) }}</div>
                <div class="responsive-time-end">{{ classTime(c.endDate) }}</div>
              </div>

              <v-avatar size="4vw" class="mr-3 flex-shrink-0 initials-avatar"
                v-if="c.professorUser && c.professorUser.userImageSmall">
                <ImageDisplay :src="c.professorUser.userImageSmall" />
              </v-avatar>

              <div class="class-details flex-grow-1 text-truncate">
                <div class="responsive-class-name font-weight-bold text-truncate">{{ c.name }}</div>
                <div class="responsive-class-meta text-truncate">
                  <span v-if="c.professorUser">{{ c.professorUser.displayName }}</span>
                  <span v-if="c.professorUser && c.location"> &middot; </span>
                  <span v-if="c.location">{{ c.location }}</span>
                </div>
                <div v-if="c.requiresRegistration" class="responsive-class-badge">
                  <v-icon :size="badgeIconSize" class="mr-1">mdi-calendar-check-outline</v-icon>
                  {{ $t('message.requires_registration') }}
                </div>
              </div>

              <div v-if="c.isActive" class="status-pill flex-shrink-0">{{ $t('message.in_class') }}</div>
              <div v-else-if="c.isNext" class="status-pill status-pill-next flex-shrink-0">
                {{ $t('message.next_class') }}
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ImageDisplay from "@/components/displays/ImageDisplay";
import Api from "@/services/Api";

export default {
  name: 'FullScreenSchedule',
  components: { ImageDisplay },
  data() {
    return {
      classLocation: this.$route.query.location,
      academyLogo: null,
      todayClasses: [],
      timeZone: null,
      darkMode: false,
      currentTime: new Date(),
      pullInterval: null,
      clockTimer: null,
      columnCount: 1,
    };
  },
  computed: {
    //The academy's local time. The device could be anywhere, and a schedule
    //showing one time while listing classes in another is just confusing.
    academyClock() {
      try {
        return new Intl.DateTimeFormat(this.$i18n.locale, {
          hour: "numeric",
          minute: "2-digit",
          timeZone: this.timeZone || undefined,
        }).format(this.currentTime);
      } catch (e) {
        return this.$d(this.currentTime, "time", this.$i18n.locale);
      }
    },
    //v-icon takes a number of pixels, and the card scales with the viewport, so
    //the badge icon has to scale with it rather than sit at a fixed size.
    badgeIconSize() {
      return Math.max(12, Math.round(window.innerWidth * 0.011));
    },
  },
  watch: {
    todayClasses: {
      handler() { this.calculateLayout(); },
      deep: true,
    },
  },
  created() {
    this.getData();
    this.pullInterval = setInterval(() => this.getData(), 10000);
  },
  mounted() {
    this.clockTimer = setInterval(() => { this.currentTime = new Date(); }, 1000);
    window.addEventListener('resize', this.calculateLayout);
    this.calculateLayout();
  },
  beforeDestroy() {
    if (this.pullInterval) clearInterval(this.pullInterval);
    if (this.clockTimer) clearInterval(this.clockTimer);
    window.removeEventListener('resize', this.calculateLayout);
  },
  methods: {
    getData() {
      const now = new Date();
      let url = "admintablet/academy/" + this.$store.state.tabletManagedAcademy +
        "/checkins-full-screen?now=" + now.getTime();
      if (this.classLocation && this.classLocation != "")
        url += '&location=' + encodeURI(this.classLocation);
      Api().get(url).then(response => {
        this.academyLogo = response.data.logo;
        this.todayClasses = response.data.todayClasses || [];
        this.timeZone = response.data.timeZone || null;
        if (response.data.scheduleWidget)
          this.darkMode = !!response.data.scheduleWidget.darkMode;
      });
    },
    classTime(value) {
      if (!value) return "";
      return this.$d(new Date(value), 'time', this.$i18n.locale);
    },
    //Same approach the check-ins widget uses: work out how many cards fit in a
    //column and spill into a second one. Capped at two, which is what reads
    //from across a room.
    calculateLayout() {
      this.$nextTick(() => {
        const container = this.$refs.scheduleContainer;
        if (!container || !this.todayClasses.length) return;
        const availHeight = container.clientHeight;
        //Measure a card that is actually on screen rather than assuming a
        //height, since the cards grow with the viewport and with how much each
        //class has to say.
        const first = container.querySelector(".schedule-item");
        const cardHeight = first ? first.offsetHeight + 10 : 110;
        const itemsPerCol = Math.max(1, Math.floor(availHeight / cardHeight));
        const needed = Math.ceil(this.todayClasses.length / itemsPerCol);
        this.columnCount = Math.max(1, Math.min(needed, 2));
      });
    },
  },
};
</script>

<style scoped>
.view-wrapper {
  height: 100vh;
  overflow: hidden;
}

/* Both themes drive the same handful of tokens, so every rule below works in
   either without being written twice. */
.theme-light {
  --bg: #f5f5f5;
  --surface: #ffffff;
  --border: #eeeeee;
  --text: #212121;
  --text-muted: #757575;
  /* The kiosk's Vuetify runs without customProperties, so there is no
     --v-primary-base to borrow. This is that theme's primary. */
  --accent: #5542f6;
}

.theme-dark {
  --bg: #0f172a;
  --surface: #162032;
  --border: #334155;
  --text: #e8eaed;
  --text-muted: #94a3b8;
  /* Lifted so it still carries on a dark background. */
  --accent: #8b7cff;
}

.view-wrapper {
  background: var(--bg);
  color: var(--text);
}

.header {
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

.responsive-header-title {
  font-size: 2.2vw;
  font-weight: 900;
}

.responsive-header-subtitle {
  font-size: 1.4vw;
  color: var(--text-muted);
}

.responsive-clock {
  font-size: 5.5vw;
}

.responsive-subheader {
  font-size: 2vw;
  color: var(--text-muted);
}

.main-content {
  min-height: 0;
}

.schedule-pane {
  min-width: 0;
}

.schedule-column-flow {
  height: 95%;
  column-fill: auto;
  column-gap: 20px;
  column-count: v-bind(columnCount);
}

.schedule-item {
  break-inside: avoid;
  display: block;
  width: 100%;
  border-radius: 12px !important;
  background-color: var(--surface) !important;
  border: 1px solid var(--border) !important;
  color: var(--text) !important;
  margin-bottom: 10px;
  /* Not a fixed height: the text scales with the viewport, and three lines of
     it at a large size overflowed a fixed card and ran into the one below. */
  min-height: 5.5vw;
}

/* The class running right now is the one people look for first. */
.schedule-item.is-active {
  border-color: var(--accent) !important;
  border-width: 2px !important;
}

.schedule-item.is-finished {
  opacity: 0.45;
}

.time-block {
  min-width: 7vw;
}

.responsive-time {
  font-size: 1.8vw;
  line-height: 1.1;
  color: var(--text);
}

.responsive-time-end {
  font-size: 1.1vw;
  color: var(--text-muted);
}

.responsive-class-name {
  font-size: 1.7vw;
  color: var(--text);
}

.responsive-class-meta {
  font-size: 1.2vw;
  color: var(--text-muted);
}

.responsive-class-badge {
  font-size: 1vw;
  color: var(--accent);
}

.status-pill {
  font-size: 1vw;
  font-weight: 700;
  text-transform: uppercase;
  padding: 0.3vw 0.9vw;
  border-radius: 999px;
  background: var(--accent);
  color: #ffffff;
}

.status-pill-next {
  background: transparent;
  border: 1px solid var(--text-muted);
  color: var(--text-muted);
}

.initials-avatar {
  background-color: var(--border) !important;
}
</style>
